import { useEffect, useState } from 'react';
import { formatiereDatumZeit } from '@uep/shared';
import { cn } from '@/lib/cn';
import { Dialog, Hinweis, Knopf } from '@/components/ui';
import { erneutVersuchen, outboxProbleme, type OutboxProblem } from './outbox';
import { speicherText } from './speicher';
import { synchronisieren, syncText, useSync, zustandAktualisieren } from './sync';

/**
 * Sync-Status in der Kopfzeile (PHASENPLAN 1.8, SPEC Kap. 12).
 *
 * Er ist immer sichtbar. Wer vor Ort steht, muss auf einen Blick erkennen, ob
 * die Arbeit der letzten Stunde schon sicher angekommen ist.
 */

const FARBEN: Record<string, string> = {
  offline: 'bg-amber-100 text-amber-900 dunkel:bg-amber-950 dunkel:text-amber-200',
  laeuft: 'bg-marke-50 text-marke-800 dunkel:bg-marke-900/50 dunkel:text-marke-200',
  wartet: 'bg-slate-100 text-slate-700 dunkel:bg-slate-800 dunkel:text-slate-200',
  fehler: 'bg-red-100 text-red-900 dunkel:bg-red-950 dunkel:text-red-200',
  synchronisiert: 'bg-emerald-50 text-emerald-800 dunkel:bg-emerald-950 dunkel:text-emerald-200',
  unbekannt: 'bg-slate-100 text-slate-600 dunkel:bg-slate-800 dunkel:text-slate-300',
};

const SYMBOLE: Record<string, string> = {
  offline: '●',
  laeuft: '⟳',
  wartet: '↑',
  fehler: '!',
  synchronisiert: '✓',
  unbekannt: '·',
};

/** Klartext für die Warteschlange — „Protocol/anlegen“ hilft niemandem. */
const ENTITAET_TEXT: Record<string, string> = {
  Protocol: 'Protokoll',
  Objekt: 'Objekt',
  Einheit: 'Einheit',
  Raum: 'Raum',
  Document: 'Dokument',
  Foto: 'Foto',
  Termin: 'Termin',
  AuditLog: 'Protokolleintrag',
};

const OPERATION_TEXT: Record<string, string> = {
  anlegen: 'anlegen',
  aendern: 'ändern',
  loeschen: 'löschen',
  abschliessen: 'abschließen',
};

const STATUS_TEXT: Record<OutboxProblem['status'], string> = {
  offen: 'wartet',
  laeuft: 'wird gesendet',
  fehler: 'Fehler — neuer Versuch folgt',
  aufgegeben: 'aufgegeben',
};

export const SyncAnzeige = () => {
  const zustand = useSync();
  const [offen, setOffen] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const [probleme, setProbleme] = useState<OutboxProblem[]>([]);

  useEffect(() => {
    void zustandAktualisieren();
  }, []);

  // Die Warteschlange wird nur gelesen, wenn der Dialog offen ist — in der
  // Kopfzeile genügt der Zähler.
  useEffect(() => {
    if (!offen) return;
    void outboxProbleme().then(setProbleme);
  }, [offen, zustand.zaehler.gesamt, zustand.art]);

  const text = syncText(zustand);
  const symbol = SYMBOLE[zustand.art] ?? '·';

  const jetztSynchronisieren = async () => {
    setLaedt(true);
    try {
      await synchronisieren(true);
    } finally {
      setLaedt(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOffen(true)}
        className={cn(
          'flex min-h-touch items-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors',
          FARBEN[zustand.art] ?? FARBEN.unbekannt,
        )}
        aria-label={`Synchronisation: ${text}. Details anzeigen.`}
        title={text}
      >
        <span
          aria-hidden="true"
          className={cn(zustand.art === 'laeuft' && 'inline-block animate-spin')}
        >
          {symbol}
        </span>
        <span className="hidden max-w-52 truncate md:inline">{text}</span>
      </button>

      <Dialog
        offen={offen}
        titel="Synchronisation"
        aufAbbrechen={() => setOffen(false)}
        abbrechenText="Schließen"
      >
        <div className="space-y-4 text-sm">
          <Hinweis
            art={
              zustand.art === 'fehler'
                ? 'fehler'
                : zustand.art === 'offline'
                  ? 'warnung'
                  : zustand.art === 'synchronisiert'
                    ? 'erfolg'
                    : 'info'
            }
          >
            {text}
          </Hinweis>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
            <dt className="text-slate-500 dunkel:text-slate-400">Verbindung</dt>
            <dd>{zustand.online ? 'Online' : 'Offline'}</dd>

            <dt className="text-slate-500 dunkel:text-slate-400">Offene Änderungen</dt>
            <dd>{zustand.zaehler.gesamt}</dd>

            {zustand.zaehler.aufgegeben > 0 && (
              <>
                <dt className="text-slate-500 dunkel:text-slate-400">Nicht gesendet</dt>
                <dd className="text-red-600 dunkel:text-red-400">{zustand.zaehler.aufgegeben}</dd>
              </>
            )}

            <dt className="text-slate-500 dunkel:text-slate-400">Letzter Abgleich</dt>
            <dd>{zustand.letzterSync ? formatiereDatumZeit(zustand.letzterSync) : 'noch nicht'}</dd>

            {zustand.speicher?.messbar && (
              <>
                <dt className="text-slate-500 dunkel:text-slate-400">Gerätespeicher</dt>
                <dd
                  className={cn(zustand.speicher.warnung && 'text-amber-700 dunkel:text-amber-300')}
                >
                  {speicherText(zustand.speicher)}
                </dd>
              </>
            )}
          </dl>

          {zustand.speicher?.gesperrt && (
            <Hinweis art="fehler" titel="Speicher fast voll">
              Es können keine weiteren Fotos aufgenommen werden. Bitte synchronisieren Sie zuerst
              die vorhandenen Protokolle — danach wird lokal wieder Platz frei.
            </Hinweis>
          )}

          {zustand.speicher?.warnung && !zustand.speicher.gesperrt && (
            <Hinweis art="warnung" titel="Speicher wird knapp">
              Der Gerätespeicher ist zu über 80 % belegt. Synchronisieren Sie, sobald Sie Netz
              haben.
            </Hinweis>
          )}

          {zustand.letzterFehler && (
            <Hinweis art="fehler" titel="Letzter Fehler">
              {zustand.letzterFehler}
            </Hinweis>
          )}

          {probleme.length > 0 && (
            <div>
              <p className="mb-2 font-medium">Warteschlange</p>
              <ul className="divide-y divide-slate-200 dunkel:divide-slate-700">
                {probleme.map((eintrag) => (
                  <li key={eintrag.id} className="py-2">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-medium">
                        {ENTITAET_TEXT[eintrag.entitaet] ?? eintrag.entitaet}{' '}
                        {OPERATION_TEXT[eintrag.operation] ?? eintrag.operation}
                      </span>
                      <span
                        className={cn(
                          'text-xs',
                          eintrag.status === 'aufgegeben'
                            ? 'text-red-600 dunkel:text-red-400'
                            : 'text-slate-500 dunkel:text-slate-400',
                        )}
                      >
                        {STATUS_TEXT[eintrag.status]}
                        {eintrag.versuche > 0 && ` · ${eintrag.versuche} Versuche`}
                      </span>
                    </div>
                    {eintrag.letzterFehler && (
                      <p className="mt-1 break-words text-xs text-slate-600 dunkel:text-slate-300">
                        {eintrag.letzterFehler}
                      </p>
                    )}
                    {eintrag.wartetBis && (
                      <p className="mt-1 text-xs text-slate-500 dunkel:text-slate-400">
                        Nächster Versuch um {formatiereDatumZeit(eintrag.wartetBis)}
                      </p>
                    )}
                    {eintrag.status === 'aufgegeben' && (
                      <Knopf
                        aussehen="still"
                        groesse="klein"
                        className="mt-1 px-0"
                        onClick={async () => {
                          await erneutVersuchen(eintrag.id);
                          await synchronisieren(false);
                          await zustandAktualisieren();
                          setProbleme(await outboxProbleme());
                        }}
                      >
                        Diesen Eintrag erneut senden
                      </Knopf>
                    )}
                  </li>
                ))}
              </ul>
              {zustand.zaehler.gesamt > probleme.length && (
                <p className="mt-2 text-xs text-slate-500 dunkel:text-slate-400">
                  … und {zustand.zaehler.gesamt - probleme.length} weitere.
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <Knopf
              onClick={() => void jetztSynchronisieren()}
              laedt={laedt}
              disabled={!zustand.online}
            >
              Jetzt synchronisieren
            </Knopf>
            {zustand.zaehler.aufgegeben > 0 && (
              <Knopf
                aussehen="sekundaer"
                onClick={async () => {
                  await erneutVersuchen();
                  await synchronisieren(false);
                  await zustandAktualisieren();
                  setProbleme(await outboxProbleme());
                }}
              >
                Fehlgeschlagene erneut versuchen
              </Knopf>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};
